import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import flowRight from 'lodash.flowright';
import { injectIntl } from 'react-intl';
import { withStyles } from '@material-ui/styles';

import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import withDetailsView from '../../../utils/withDetailsView';
import CustomTableTitle from '../../../partials/CustomTableTitle';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import tableColumns from './tableColumns';
import { doesArrayHasLength } from '../../../utils/isDefined';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import { actions as reduxActions } from '../reducers';
import { actions as datalimitMultieditReduxActions } from '../../../modals/datalimitMultiedit/reducers';
import styles from '../styles';
import { getDataLimits } from '../actions/getDataLimits';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import filterCustomJobs from '../../../utils/filterCustomJobs';
import CustomJobsMenu from '../../../partials/customJobsMenu/CustomJobsMenu';
import { Link } from 'react-router-dom';
import CustomIconButton from '../../../partials/tableCustomButtons/CustomIconButton';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';

export function DataLimitsTable({
  intl,
  dataLimits,
  isLoading,
  clientId,
  columns,
  setColumns,
  openMultiedit,
  classes,
  getDataLimits,
  clientName,
  customJobs
}) {
  const [filtering, setFiltering] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  const tableDefaultColumns = useMemo(() => {
    return tableColumns({ clientId, setRefresh });
  }, [clientId]);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'Datalimits-2',
    setColumns,
    tableDefaultColumns,
    language
  });

  useEffect(() => {
    getDataLimits(clientId, 'current');
  }, [getDataLimits, clientId]);

  useEffect(() => {
    if (refresh) {
      getDataLimits(clientId, 'current');
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const mappedJobs = useMemo(() => {
    return filterCustomJobs(customJobs, 'DataLimits');
  }, [customJobs]);

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: intl.formatMessage({ id: 'BUTTON.FILTER' })
        },
        {
          icon: () => (
            <Link
              to={{
                pathname: `/${clientId}/dataLimits/usageTable`
              }}
            >
              <i
                className="fas fa-chart-area"
                style={{ color: 'rgba(0, 0, 0, 0.54)' }}
              ></i>
            </Link>
          ),
          onClick: () => {},
          isFreeAction: true,
          tooltip: intl.formatMessage({ id: 'DATA_LIMITS.USAGE_TABLE' })
        },
        {
          tooltip: intl.formatMessage({ id: 'BUTTON.MULTIEDIT' }),
          icon: () => <CustomIconButton iconClassName="far fa-edit" />,
          onClick: (evt, data) => openMultiedit(data)
        }
      ];

      if (doesArrayHasLength(mappedJobs)) {
        buttons = [
          ...buttons,
          {
            icon: () => <CustomJobsMenu customJobs={mappedJobs} />,
            onClick: () => {},
            isFreeAction: true
          }
        ];
      }

      buttons = [
        ...buttons,
        {
          icon: () => <RefreshButton tableHeader={true} />,
          onClick: () => setRefresh(true),
          isFreeAction: true
        }
      ];

      return buttons;
    },
    // eslint-disable-next-line
    [filtering, customJobs, clientId, dataLimits]
  );

  const options = useMemo(() => {
    return {
      ...mapTableOptions({ tableName: 'datalimits', clientName }),
      filtering,
      header: doesArrayHasLength(dataLimits),
      selection: true,
      pageSize
    };
  }, [filtering, dataLimits, clientName, pageSize]);

  return (
    <MaterialTable
      components={{
        Toolbar: props => (
          <MTableToolbar
            classes={{
              highlight: classes.toolbarHighlight
            }}
            {...props}
          />
        )
      }}
      title={
        <CustomTableTitle
          text="MENU.DATA_LIMITS"
          icon={<i className="fas fa-chart-bar"></i>}
          isLoading={isLoading}
        ></CustomTableTitle>
      }
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={dataLimits}
      options={options}
      localization={localization}
      onChangeColumnHidden={onChangeColumnHidden}
      onRowsPerPageChange={onChangeRowsPerPage}
    />
  );
}

const mapStatesToProps = ({ dataLimits, loader, selectClient, auth }) => {
  return {
    ...dataLimits,
    isLoading: loader.isSpinVisible,
    clientId: selectClient.selectedClient.id,
    clientName: selectClient.selectedClient.name,
    customJobs: auth.customJobs
  };
};

const mapDispatchToProps = {
  setColumns: reduxActions.setColumns,
  openMultiedit: datalimitMultieditReduxActions.openDialog,
  getDataLimits
};

export default flowRight(
  injectIntl,
  withDetailsView,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(DataLimitsTable);
